import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  ArrowLeftIcon,
  BoltIcon,
  PlusIcon,
  PlayIcon,
  PauseIcon,
  TrashIcon,
  PencilIcon,
  EnvelopeIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  XMarkIcon,
  ChevronDownIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const TRIGGER_TYPES = [
  { value: 'email_received', label: 'Email Received', icon: EnvelopeIcon, description: 'When an email arrives matching conditions' },
  { value: 'email_from_vip', label: 'VIP Email', icon: UserGroupIcon, description: 'When a VIP contact sends an email' },
  { value: 'sentiment_negative', label: 'Negative Sentiment', icon: ExclamationTriangleIcon, description: 'When AI detects negative sentiment' },
  { value: 'no_response', label: 'No Response', icon: ClockIcon, description: 'When no response is sent within a time limit' },
  { value: 'keyword_match', label: 'Keyword Match', icon: BoltIcon, description: 'When email contains specific keywords' },
];

const ACTION_TYPES = [
  { value: 'escalate', label: 'Escalate to Manager' },
  { value: 'auto_reply', label: 'Send Auto-Reply' },
  { value: 'assign_agent', label: 'Assign to Department Agent' },
  { value: 'tag', label: 'Add Tag/Label' },
  { value: 'forward', label: 'Forward to Email' },
  { value: 'notify', label: 'Send Notification' },
  { value: 'create_task', label: 'Create Task' },
];

const AIRules = ({ showNotification }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger: 'email_received',
    conditions: { from: '', subject: '', keywords: '', domain: '' },
    action: 'escalate',
    actionConfig: {},
    priority: 'medium',
    enabled: true,
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/ai/rules');
      setRules(response.data.data || []);
    } catch (error) {
      // API might not exist yet — show empty state

      setRules([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      trigger: 'email_received',
      conditions: { from: '', subject: '', keywords: '', domain: '' },
      action: 'escalate',
      actionConfig: {},
      priority: 'medium',
      enabled: true,
    });
    setEditingRule(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showNotification?.('Please enter a rule name', 'error');
      return;
    }

    try {
      setSaving(true);
      if (editingRule) {
        await axiosInstance.put(`/api/ai/rules/${editingRule._id}`, formData);
        showNotification?.('Rule updated', 'success');
      } else {
        await axiosInstance.post('/api/ai/rules', formData);
        showNotification?.('Rule created', 'success');
      }
      setShowCreateModal(false);
      resetForm();
      fetchRules();
    } catch (error) {
      showNotification?.(error.response?.data?.message || 'Failed to save rule', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleRule = async (rule) => {
    try {
      await axiosInstance.put(`/api/ai/rules/${rule._id}`, { ...rule, enabled: !rule.enabled });
      setRules(prev => prev.map(r => r._id === rule._id ? { ...r, enabled: !r.enabled } : r));
      showNotification?.(`Rule ${rule.enabled ? 'disabled' : 'enabled'}`, 'success');
    } catch (error) {
      showNotification?.('Failed to update rule', 'error');
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      await axiosInstance.delete(`/api/ai/rules/${ruleId}`);
      setRules(prev => prev.filter(r => r._id !== ruleId));
      showNotification?.('Rule deleted', 'success');
    } catch (error) {
      showNotification?.('Failed to delete rule', 'error');
    }
  };

  const editRule = (rule) => {
    setFormData({
      name: rule.name,
      description: rule.description || '',
      trigger: rule.trigger,
      conditions: rule.conditions || { from: '', subject: '', keywords: '', domain: '' },
      action: rule.action,
      actionConfig: rule.actionConfig || {},
      priority: rule.priority || 'medium',
      enabled: rule.enabled,
    });
    setEditingRule(rule);
    setShowCreateModal(true);
  };

  const selectedTrigger = TRIGGER_TYPES.find(t => t.value === formData.trigger);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#111111]">
        <div className="max-w-5xl mx-auto px-6 py-3.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/ai-dashboard')}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-[#1a1a1a] rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Automation Rules
                </h1>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  If/then rules for email automation and delegation
                </p>
              </div>
            </div>
            <button
              onClick={() => { resetForm(); setShowCreateModal(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              New Rule
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 py-6">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 bg-gray-100 dark:bg-[#1a1a1a] rounded-xl animate-pulse"></div>
              ))}
            </div>
          ) : rules.length === 0 ? (
            /* Empty state */
            <div className="text-center py-16">
              <div className="w-14 h-14 rounded-2xl bg-yellow-50 dark:bg-yellow-900/20 flex items-center justify-center mx-auto mb-4">
                <BoltIcon className="w-7 h-7 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                No automation rules yet
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 max-w-sm mx-auto mb-5">
                Create rules to automatically handle emails, escalate to managers, assign to department agents, and more.
              </p>
              <button
                onClick={() => { resetForm(); setShowCreateModal(true); }}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Create First Rule
              </button>

              {/* Example rules */}
              <div className="mt-10 text-left max-w-lg mx-auto">
                <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Example rules you can create</p>
                <div className="space-y-2">
                  {[
                    { trigger: 'VIP Email', action: 'Escalate to Manager', desc: 'When CEO or board members email, escalate immediately' },
                    { trigger: 'Negative Sentiment', action: 'Assign to Support Agent', desc: 'Angry customer emails go to the support department' },
                    { trigger: 'No Response (2h)', action: 'Send Reminder', desc: 'If no reply within 2 hours, notify the assigned agent' },
                    { trigger: 'Keyword "invoice"', action: 'Assign to Finance Agent', desc: 'Route invoice-related emails to finance' },
                  ].map((example, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-[#1a1a1a] border border-gray-100 dark:border-gray-800">
                      <div className="flex-shrink-0 w-1 h-8 rounded-full bg-yellow-400 dark:bg-yellow-500"></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-medium text-gray-900 dark:text-white">{example.trigger}</span>
                          <span className="text-gray-400">→</span>
                          <span className="font-medium text-yellow-700 dark:text-yellow-400">{example.action}</span>
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{example.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Rules list */
            <div className="space-y-2">
              {rules.map((rule) => {
                const trigger = TRIGGER_TYPES.find(t => t.value === rule.trigger);
                const action = ACTION_TYPES.find(a => a.value === rule.action);
                const TriggerIcon = trigger?.icon || BoltIcon;

                return (
                  <div
                    key={rule._id}
                    className={`rounded-xl border p-4 transition-all ${
                      rule.enabled
                        ? 'bg-white dark:bg-[#1a1a1a] border-gray-200 dark:border-gray-800'
                        : 'bg-gray-50 dark:bg-[#131313] border-gray-200 dark:border-gray-800 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${rule.enabled ? 'bg-yellow-50 dark:bg-yellow-900/20' : 'bg-gray-100 dark:bg-[#222222]'}`}>
                        <TriggerIcon className={`w-4 h-4 ${rule.enabled ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-400'}`} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">{rule.name}</h3>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            rule.priority === 'high' ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400' :
                            rule.priority === 'low' ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400' :
                            'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                          }`}>
                            {rule.priority}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                          <span>{trigger?.label || rule.trigger}</span>
                          <span className="text-gray-300 dark:text-gray-600">→</span>
                          <span>{action?.label || rule.action}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => toggleRule(rule)}
                          className={`p-1.5 rounded-lg transition-colors ${
                            rule.enabled
                              ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                              : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-[#222222]'
                          }`}
                          title={rule.enabled ? 'Disable' : 'Enable'}
                        >
                          {rule.enabled ? <PlayIcon className="w-4 h-4" /> : <PauseIcon className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => editRule(rule)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteRule(rule._id)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#161616] rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden border border-gray-200 dark:border-gray-800 shadow-2xl">
            {/* Modal header */}
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                {editingRule ? 'Edit Rule' : 'New Automation Rule'}
              </h2>
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="p-1 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
              >
                <XMarkIcon className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Modal body */}
            <div className="px-5 py-4 space-y-4 overflow-y-auto max-h-[60vh]">
              {/* Rule Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Rule Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Escalate VIP emails"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1e1e1e] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What does this rule do?"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1e1e1e] text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Trigger */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">When (Trigger)</label>
                <div className="grid grid-cols-1 gap-1.5">
                  {TRIGGER_TYPES.map((trigger) => {
                    const Icon = trigger.icon;
                    return (
                      <label
                        key={trigger.value}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                          formData.trigger === trigger.value
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/10 dark:border-blue-600'
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
                        }`}
                      >
                        <input
                          type="radio"
                          name="trigger"
                          value={trigger.value}
                          checked={formData.trigger === trigger.value}
                          onChange={(e) => setFormData({ ...formData, trigger: e.target.value })}
                          className="sr-only"
                        />
                        <Icon className={`w-4 h-4 ${formData.trigger === trigger.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
                        <div>
                          <p className="text-xs font-medium text-gray-900 dark:text-white">{trigger.label}</p>
                          <p className="text-[10px] text-gray-500 dark:text-gray-400">{trigger.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Conditions based on trigger */}
              {(formData.trigger === 'email_received' || formData.trigger === 'keyword_match') && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Conditions</label>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={formData.conditions.from}
                      onChange={(e) => setFormData({ ...formData, conditions: { ...formData.conditions, from: e.target.value } })}
                      placeholder="From (email or domain)"
                      className="w-full px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1e1e1e] text-gray-900 dark:text-white"
                    />
                    <input
                      type="text"
                      value={formData.conditions.subject}
                      onChange={(e) => setFormData({ ...formData, conditions: { ...formData.conditions, subject: e.target.value } })}
                      placeholder="Subject contains..."
                      className="w-full px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1e1e1e] text-gray-900 dark:text-white"
                    />
                    <input
                      type="text"
                      value={formData.conditions.keywords}
                      onChange={(e) => setFormData({ ...formData, conditions: { ...formData.conditions, keywords: e.target.value } })}
                      placeholder="Keywords (comma-separated)"
                      className="w-full px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1e1e1e] text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
              )}

              {formData.trigger === 'no_response' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Time limit (minutes)</label>
                  <input
                    type="number"
                    value={formData.conditions.minutes || 120}
                    onChange={(e) => setFormData({ ...formData, conditions: { ...formData.conditions, minutes: parseInt(e.target.value) } })}
                    placeholder="120"
                    className="w-full px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1e1e1e] text-gray-900 dark:text-white"
                  />
                </div>
              )}

              {/* Action */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Then (Action)</label>
                <select
                  value={formData.action}
                  onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1e1e1e] text-gray-900 dark:text-white"
                >
                  {ACTION_TYPES.map(a => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Priority</label>
                <div className="flex gap-2">
                  {['low', 'medium', 'high'].map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setFormData({ ...formData, priority: p })}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-lg border transition-colors capitalize ${
                        formData.priority === p
                          ? p === 'high' ? 'border-red-500 bg-red-50 dark:bg-red-900/10 text-red-700 dark:text-red-400'
                          : p === 'low' ? 'border-gray-500 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                          : 'border-blue-500 bg-blue-50 dark:bg-blue-900/10 text-blue-700 dark:text-blue-400'
                          : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal footer */}
            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-2">
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a1a1a] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.name.trim()}
                className="px-4 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-40 transition-colors flex items-center gap-1.5"
              >
                {saving ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white dark:border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : editingRule ? 'Update Rule' : 'Create Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIRules;
